<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import Card from 'primevue/card';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import UserAvatar from '@/components/UserAvatar.vue';
import type { MemberUser, VirtualGift } from '@/types/api';
import { GIFT_META } from '@/types/api';
import { fetchGifts, fetchMembers, sendGift } from '@/lib/apiClient';
import { useAuth } from '@/composables/useAuth';

const route = useRoute();
const toast = useToast();
const { token, user } = useAuth();

const loading = ref(true);
const submitting = ref(false);
const gifts = ref<VirtualGift[]>([]);
const members = ref<MemberUser[]>([]);
const selectedGift = ref('rose');
const selectedRecipient = ref<number | null>(null);
const message = ref('');
const isPublic = ref(false);
const isAnonymous = ref(false);

const recipientOptions = computed(() =>
  members.value
    .filter((member) => member.id !== user.value?.id)
    .map((member) => ({ label: member.nickname, value: member.id })),
);

async function loadData() {
  if (!token.value) return;
  const [giftsResponse, membersResponse] = await Promise.all([
    fetchGifts(token.value),
    fetchMembers(token.value),
  ]);
  gifts.value = giftsResponse.gifts;
  members.value = membersResponse.members;
}

onMounted(async () => {
  try {
    await loadData();
    const toParam = Number(route.query.to);
    if (toParam && members.value.some((member) => member.id === toParam)) {
      selectedRecipient.value = toParam;
    }
  } finally {
    loading.value = false;
  }
});

watch(
  () => route.query.to,
  (value) => {
    const toParam = Number(value);
    if (toParam && members.value.some((member) => member.id === toParam)) {
      selectedRecipient.value = toParam;
    }
  },
);

async function submitGift() {
  if (!token.value || !selectedRecipient.value) {
    toast.add({
      severity: 'warn',
      summary: 'Выберите получателя',
      life: 2500,
    });
    return;
  }

  submitting.value = true;

  try {
    await sendGift(token.value, {
      giftType: selectedGift.value,
      message: message.value,
      toUserId: selectedRecipient.value,
      isPublic: isPublic.value,
      isAnonymous: isAnonymous.value,
    });
    message.value = '';
    isPublic.value = false;
    isAnonymous.value = false;
    gifts.value = (await fetchGifts(token.value)).gifts;
    toast.add({ severity: 'success', summary: 'Подарок отправлен', life: 2500 });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось отправить',
      life: 3000,
    });
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="page">
    <section class="page__hero">
      <h1>Виртуальные подарки</h1>
      <p>Выберите получателя, подарок и пожелание — ссылку на сертификат или любой текст.</p>
    </section>

    <Card>
      <template #title>Отправить подарок</template>
      <template #content>
        <label class="field">
          <span>Кому подарить</span>
          <Select
            v-model="selectedRecipient"
            :options="recipientOptions"
            option-label="label"
            option-value="value"
            placeholder="Выберите пользователя"
            filter
            fluid
          />
        </label>

        <div class="gift-picker">
          <button
            v-for="(meta, key) in GIFT_META"
            :key="key"
            type="button"
            class="gift-picker__item"
            :class="{ 'gift-picker__item--active': selectedGift === key }"
            :style="{ borderColor: selectedGift === key ? meta.color : '#fbcfe8' }"
            @click="selectedGift = key"
          >
            <span class="gift-picker__emoji">{{ meta.emoji }}</span>
            <span>{{ meta.label }}</span>
          </button>
        </div>

        <label class="field">
          <span>Сообщение к подарку</span>
          <Textarea
            v-model="message"
            rows="4"
            auto-resize
            placeholder="Пожелание, ссылка на сертификат, промокод — что угодно..."
            fluid
          />
        </label>

        <div class="flags">
          <label class="flags__item">
            <Checkbox v-model="isPublic" binary input-id="gift-public" />
            <span>Публичное сообщение — другие увидят его при наведении на подарок</span>
          </label>
          <label class="flags__item">
            <Checkbox v-model="isAnonymous" binary input-id="gift-anonymous" />
            <span>Анонимный подарок — отправитель не будет показан</span>
          </label>
        </div>
      </template>
      <template #footer>
        <Button label="Подарить" icon="pi pi-gift" :loading="submitting" @click="submitGift" />
      </template>
    </Card>

    <ProgressSpinner v-if="loading" />

    <section v-else>
      <h2>Лента подарков</h2>
      <div class="gift-feed">
        <Card v-for="gift in gifts" :key="gift.id" class="gift-card">
          <template #content>
            <div class="gift-card__head">
              <UserAvatar
                v-if="gift.nickname"
                :nickname="gift.nickname"
                :avatar="gift.avatar"
              />
              <div v-else class="gift-card__anon">🎭</div>
              <div>
                <strong>
                  {{ gift.nickname ?? 'Аноним' }}
                  <template v-if="gift.toNickname"> → {{ gift.toNickname }}</template>
                </strong>
                <small>{{ new Date(gift.createdAt).toLocaleString('ru-RU') }}</small>
              </div>
              <span class="gift-card__emoji">{{ GIFT_META[gift.giftType]?.emoji ?? '🎁' }}</span>
            </div>
            <p v-if="gift.message">{{ gift.message }}</p>
            <div v-else-if="gift.isPublic" class="gift-card__hint">Публичное сообщение — смотрите на профиле</div>
          </template>
        </Card>
      </div>
    </section>
  </div>
</template>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.page__hero h1 {
  margin: 0 0 0.35rem;
  color: #831843;
}

.page__hero p {
  margin: 0;
  color: #9d174d;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #831843;
  font-weight: 600;
}

.gift-picker {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.gift-picker__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  padding: 1rem;
  border: 2px solid #fbcfe8;
  border-radius: 1rem;
  background: #fff7fb;
  cursor: pointer;
  color: #831843;
  font-weight: 600;
}

.gift-picker__item--active {
  background: #fdf2f8;
}

.gift-picker__emoji {
  font-size: 1.8rem;
}

.flags {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.flags__item {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  color: #9d174d;
  cursor: pointer;
  line-height: 1.4;
}

.gift-feed {
  display: grid;
  gap: 1rem;
}

.gift-card__head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.gift-card__head small {
  display: block;
  color: #be185d;
}

.gift-card__emoji {
  margin-left: auto;
  font-size: 2rem;
}

.gift-card__anon {
  width: 2.5rem;
  height: 2.5rem;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #fce7f3;
  font-size: 1.2rem;
}

.gift-card__hint {
  margin: 0;
  color: #be185d;
  font-size: 0.9rem;
  font-style: italic;
}
</style>
